import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, RefreshCw, ShieldAlert, Trash2, UserX, Gift, CalendarMinus, ArrowDownCircle, ArrowUpCircle, Building2, Calendar, FileDown, PauseCircle, PlayCircle, Clock, Unlock } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  sale_deletion: { label: "Venda excluída", icon: Trash2, color: "bg-rose-100 text-rose-700", accent: "border-l-rose-500" },
  cash_movement: { label: "Mov. de caixa", icon: ArrowDownCircle, color: "bg-amber-100 text-amber-700", accent: "border-l-amber-500" },
  cash_reopen: { label: "Caixa reaberto", icon: Unlock, color: "bg-violet-100 text-violet-700", accent: "border-l-violet-500" },
  bonus_days: { label: "Dias bônus", icon: Gift, color: "bg-emerald-100 text-emerald-700", accent: "border-l-emerald-500" },
  suspend: { label: "Aluno suspenso", icon: PauseCircle, color: "bg-orange-100 text-orange-700", accent: "border-l-orange-500" },
  unsuspend: { label: "Aluno reativado", icon: PlayCircle, color: "bg-sky-100 text-sky-700", accent: "border-l-sky-500" },
  off_hours_access: { label: "Acesso fora do horário", icon: Clock, color: "bg-amber-100 text-amber-800", accent: "border-l-amber-600" },
  days_removed: { label: "Dias retirados", icon: CalendarMinus, color: "bg-rose-100 text-rose-700", accent: "border-l-rose-500" },
  student_deletion: { label: "Aluno excluído", icon: UserX, color: "bg-red-100 text-red-700", accent: "border-l-red-600" },
};

const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso || "—"; } };
const fmtBRL = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Auditoria() {
  const [events, setEvents] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all"); // all | sale_deletion | cash_movement | bonus_days
  const [unitFilter, setUnitFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (unitFilter !== "all") params.unit_id = unitFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [e, u] = await Promise.all([
        api.get("/audit/events", { params }),
        api.get("/units"),
      ]);
      setEvents(e.data.events || []);
      setUnits(u.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar auditoria");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    let list = events;
    if (tab !== "all") list = list.filter(e => e.kind === tab);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(e =>
        (e.reason || "").toLowerCase().includes(q) ||
        (e.actor_name || "").toLowerCase().includes(q) ||
        (e.student_name || "").toLowerCase().includes(q) ||
        (e.plan_name || "").toLowerCase().includes(q) ||
        (e.unit_name || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [events, tab, search]);

  const totals = useMemo(() => {
    const total_deleted = events.filter(e => e.kind === "sale_deletion").reduce((s, e) => s + (e.amount || 0), 0);
    const total_sangria = events.filter(e => e.kind === "cash_movement" && e.sub === "sangria").reduce((s, e) => s + (e.amount || 0), 0);
    const total_suprimento = events.filter(e => e.kind === "cash_movement" && e.sub === "suprimento").reduce((s, e) => s + (e.amount || 0), 0);
    const total_bonus = events.filter(e => e.kind === "bonus_days").reduce((s, e) => s + (e.meta?.days || 0), 0);
    const total_suspend = events.filter(e => e.kind === "suspend").length;
    const total_unsuspend = events.filter(e => e.kind === "unsuspend").length;
    const total_off_hours = events.filter(e => e.kind === "off_hours_access").length;
    return { total_deleted, total_sangria, total_suprimento, total_bonus, total_suspend, total_unsuspend, total_off_hours };
  }, [events]);

  const exportCSV = () => {
    const rows = [["Data", "Tipo", "Sub", "Motivo", "Valor", "Aluno", "Plano", "Unidade", "Usuário"]];
    filtered.forEach(e => {
      rows.push([
        fmtDT(e.at),
        KIND_META[e.kind]?.label || e.kind,
        e.sub || "",
        (e.reason || "").replace(/;/g, ","),
        e.amount != null ? e.amount.toFixed(2) : (e.meta?.days ? `+${e.meta.days} dias` : ""),
        e.student_name || "",
        e.plan_name || "",
        e.unit_name || "",
        e.actor_name || "",
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `auditoria_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Auditoria"
        subtitle="Log completo de exclusões de vendas, movimentações de caixa (sangria/suprimento) e concessão de dias bônus"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={exportCSV} disabled={filtered.length === 0} data-testid="audit-export-csv">
              <FileDown className="w-4 h-4 mr-2"/>Exportar CSV
            </Button>
            <Button onClick={load} variant="outline" disabled={loading} data-testid="audit-refresh">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}/>Atualizar
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4 border-l-4 border-l-rose-500">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase"><Trash2 className="w-4 h-4"/>Vendas excluídas</div>
          <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1" data-testid="audit-kpi-deleted">{fmtBRL(totals.total_deleted)}</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-amber-500">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase"><ArrowDownCircle className="w-4 h-4"/>Sangria</div>
          <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1" data-testid="audit-kpi-sangria">{fmtBRL(totals.total_sangria)}</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-emerald-500">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase"><ArrowUpCircle className="w-4 h-4"/>Suprimento</div>
          <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1" data-testid="audit-kpi-suprimento">{fmtBRL(totals.total_suprimento)}</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-sky-500">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase"><Gift className="w-4 h-4"/>Dias bônus</div>
          <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1" data-testid="audit-kpi-bonus">+{totals.total_bonus}</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-orange-500">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase"><PauseCircle className="w-4 h-4"/>Suspensões</div>
          <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1" data-testid="audit-kpi-suspend">{totals.total_suspend}</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-sky-400">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase"><PlayCircle className="w-4 h-4"/>Reativações</div>
          <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1" data-testid="audit-kpi-unsuspend">{totals.total_unsuspend}</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-amber-600">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase"><Clock className="w-4 h-4"/>Fora do horário</div>
          <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1" data-testid="audit-kpi-off-hours">{totals.total_off_hours}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 border-slate-200">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="min-w-[180px]">
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block flex items-center gap-1"><Building2 className="w-3 h-3"/>Unidade</label>
            <Select value={unitFilter} onValueChange={setUnitFilter}>
              <SelectTrigger data-testid="audit-unit-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as unidades</SelectItem>
                {units.map(u => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block flex items-center gap-1"><Calendar className="w-3 h-3"/>De</label>
            <Input type="date" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} data-testid="audit-date-from"/>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block flex items-center gap-1"><Calendar className="w-3 h-3"/>Até</label>
            <Input type="date" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} data-testid="audit-date-to"/>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Buscar motivo/aluno/usuário</label>
            <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Ex: erro digitação, João..." data-testid="audit-search"/>
          </div>
          <Button onClick={load} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="audit-apply-filters">Aplicar</Button>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="audit-tabs">
          <TabsTrigger value="all" data-testid="audit-tab-all">Todos ({events.length})</TabsTrigger>
          <TabsTrigger value="sale_deletion" data-testid="audit-tab-sale-deletion">Vendas excluídas ({events.filter(e=>e.kind==="sale_deletion").length})</TabsTrigger>
          <TabsTrigger value="cash_movement" data-testid="audit-tab-cash-movement">Sangria/Suprimento ({events.filter(e=>e.kind==="cash_movement").length})</TabsTrigger>
          <TabsTrigger value="bonus_days" data-testid="audit-tab-bonus-days">Dias bônus ({events.filter(e=>e.kind==="bonus_days").length})</TabsTrigger>
          <TabsTrigger value="suspend" data-testid="audit-tab-suspend">Suspensões ({events.filter(e=>e.kind==="suspend").length})</TabsTrigger>
          <TabsTrigger value="unsuspend" data-testid="audit-tab-unsuspend">Reativações ({events.filter(e=>e.kind==="unsuspend").length})</TabsTrigger>
          <TabsTrigger value="off_hours_access" data-testid="audit-tab-off-hours">Fora do horário ({events.filter(e=>e.kind==="off_hours_access").length})</TabsTrigger>
          <TabsTrigger value="days_removed" data-testid="audit-tab-days-removed">Dias retirados ({events.filter(e=>e.kind==="days_removed").length})</TabsTrigger>
          <TabsTrigger value="student_deletion" data-testid="audit-tab-student-deletion">Alunos excluídos ({events.filter(e=>e.kind==="student_deletion").length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Event list */}
      <Card className="border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-8 h-8 text-rose-500 animate-spin mx-auto"/></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-2"/>
            <p className="text-slate-500 font-semibold">Nenhum evento de auditoria encontrado</p>
            <p className="text-xs text-slate-400 mt-1">Ajuste os filtros ou aguarde a primeira exclusão/movimentação</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="audit-events-list">
            {filtered.map((e) => {
              const meta = KIND_META[e.kind] || {};
              const Icon = meta.icon || ShieldAlert;
              const isSuprimento = e.kind === "cash_movement" && e.sub === "suprimento";
              const accent = isSuprimento ? "border-l-emerald-500" : meta.accent;
              return (
                <div key={e.id} className={`p-4 border-l-4 ${accent} hover:bg-slate-50 transition-colors`} data-testid={`audit-event-${e.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
                      <Icon className="w-5 h-5"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-heading text-sm font-extrabold text-slate-900 uppercase">{meta.label}</span>
                            {e.sub && <Badge className={`text-[10px] font-bold uppercase ${e.sub === "sangria" ? "bg-rose-100 text-rose-700 hover:bg-rose-100" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"}`}>{e.sub}</Badge>}
                            {e.unit_name && <Badge variant="outline" className="text-[10px] font-semibold"><Building2 className="w-2.5 h-2.5 mr-1"/>{e.unit_name}</Badge>}
                          </div>
                          <p className="text-sm text-slate-700 mt-1 italic">"{e.reason || "(sem motivo)"}"</p>
                          {e.student_name && <p className="text-xs text-slate-500 mt-1">Aluno: <strong className="text-slate-700">{e.student_name}</strong>{e.plan_name && <> · Plano: <strong className="text-slate-700">{e.plan_name}</strong></>}</p>}
                          {e.kind === "sale_deletion" && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              {e.meta?.reverted ? (
                                <>Vencimento revertido: <span className="line-through">{e.meta.prev_membership_end ? new Date(e.meta.prev_membership_end).toLocaleDateString("pt-BR") : "—"}</span> → <strong>{e.meta.new_membership_end ? new Date(e.meta.new_membership_end).toLocaleDateString("pt-BR") : "—"}</strong>{" "}
                                  {e.meta?.still_active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[9px]">Ainda ativo</Badge> : <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 text-[9px]">Inativo</Badge>}
                                </>
                              ) : <>Sem reversão de dias</>}
                            </p>
                          )}
                          {e.kind === "bonus_days" && (
                            <p className="text-xs text-slate-500 mt-0.5">+{e.meta?.days} dias concedidos · Novo vencimento: <strong>{e.meta?.new_end ? new Date(e.meta.new_end).toLocaleDateString("pt-BR") : "—"}</strong></p>
                          )}
                          {e.kind === "suspend" && (
                            <p className="text-xs text-slate-500 mt-0.5">Categoria: <strong className="text-slate-700">{e.meta?.category || "—"}</strong> · Dias guardados: <strong className="text-orange-600">{e.meta?.days_saved ?? 0}</strong></p>
                          )}
                          {e.kind === "unsuspend" && (
                            <p className="text-xs text-slate-500 mt-0.5">Dias restaurados: <strong className="text-sky-600">{e.meta?.days_restored ?? 0}</strong></p>
                          )}
                          {e.kind === "days_removed" && (
                            <p className="text-xs text-slate-500 mt-0.5" data-testid={`audit-days-removed-${e.id}`}>−{e.meta?.days} dias retirados · Vencimento: {e.meta?.previous_end ? new Date(e.meta.previous_end).toLocaleDateString("pt-BR") : "—"} → <strong>{e.meta?.new_end ? new Date(e.meta.new_end).toLocaleDateString("pt-BR") : "—"}</strong></p>
                          )}
                          {e.kind === "student_deletion" && (
                            <p className="text-xs text-slate-500 mt-0.5" data-testid={`audit-student-deletion-${e.id}`}>
                              Aluno: <strong className="text-slate-700">{e.deleted_student_name || "—"}</strong>
                              {e.meta?.cpf && <> · CPF: <strong className="text-slate-700">{e.meta.cpf}</strong></>}
                              {e.meta?.membership_end && <> · Vencimento: <strong className="text-slate-700">{new Date(e.meta.membership_end).toLocaleDateString("pt-BR")}</strong></>}
                              {" · "}{e.meta?.was_active ? "Estava ativo" : "Estava inativo"}
                            </p>
                          )}
                          {e.kind === "off_hours_access" && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              Perfil: <strong className="text-slate-700 uppercase">{e.meta?.role || "—"}</strong>
                              {e.meta?.email && <> · Email: <strong className="text-slate-700">{e.meta.email}</strong></>}
                              {e.meta?.ip && <> · IP: <strong className="text-slate-700">{e.meta.ip}</strong></>}
                              {e.meta?.at_local && <> · Horário local: <strong className="text-amber-700">{new Date(e.meta.at_local).toLocaleString("pt-BR")}</strong></>}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          {e.amount != null && (
                            <div className={`font-heading text-lg font-extrabold ${e.kind === "sale_deletion" ? "text-rose-600" : (isSuprimento ? "text-emerald-600" : "text-amber-600")}`}>
                              {e.kind === "sale_deletion" || e.sub === "sangria" ? "−" : "+"}{fmtBRL(e.amount)}
                            </div>
                          )}
                          {e.kind === "bonus_days" && (
                            <div className="font-heading text-lg font-extrabold text-sky-600">+{e.meta?.days} dias</div>
                          )}
                          {e.kind === "suspend" && (
                            <div className="font-heading text-lg font-extrabold text-orange-600">−{e.meta?.days_saved ?? 0} dias</div>
                          )}
                          {e.kind === "unsuspend" && (
                            <div className="font-heading text-lg font-extrabold text-sky-500">+{e.meta?.days_restored ?? 0} dias</div>
                          )}
                          <div className="text-[11px] text-slate-500">{fmtDT(e.at)}</div>
                          <div className="text-[11px] text-slate-400">por <strong>{e.actor_name || "—"}</strong></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
